import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Chip
} from "@mui/material";
import { getDoc, doc, deleteDoc, setDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useLocation } from "react-router-dom";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DynamicUploadModal from "../../../components/UploadModal";
import SymptomSelector from "../../../components/topicCategorySelector/SymptomSelector";

const Fullview = () => {
  const [openModal, setOpenModal] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [data, setData] = useState({ symptoms: [], symptom_ids: [] });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [newSymptom, setNewSymptom] = useState("");
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get("uid");
    if (uid) {
      fetchData(uid);
    }
  }, [location.search]);

  const fetchData = async (uid) => {
    try {
      const docRef = doc(db, "specialities", uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setData(docSnap.data());
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const handleUploadSuccess = (url) => {
    setUploadedImageUrl(url);
    setData((prevData) => ({
      ...prevData,
      imageUrl: url,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const uid = data.id;
      const docRef = doc(db, "specialities", uid);
      await setDoc(docRef, {
        ...data,
        symptoms: data.symptoms,  // Save the full symptom objects
        symptom_ids: data.symptom_ids, // Save the symptom ids
      });
      setAlertSeverity("success");
      setAlertMessage("Data saved successfully!");
    } catch (err) {
      console.error("Error saving data:", err);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${err.message}`);
    } finally {
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    try {
      const uid = data.id;
      await deleteDoc(doc(db, "specialities", uid));
      setAlertSeverity("success");
      setAlertMessage("Specialities deleted successfully!");
      setData({ symptoms: [], symptom_ids: [] });
      handleDeleteDialogClose();
    } catch (err) {
      console.error("Error deleting specialities:", err);
      setAlertSeverity("error");
      setAlertMessage(`Error: ${err.message}`);
    } finally {
      setShowAlert(true);
    }
  };

  const handleDeleteDialogOpen = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setDeleteConfirmation("");
  };

  const handleDeleteConfirmationChange = (e) => {
    setDeleteConfirmation(e.target.value);
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const handleSelectSymptom = (selectedSymptom) => {
    setData((prevData) => {
      const existingIds = prevData.symptom_ids;
      if (!existingIds.includes(selectedSymptom.id)) {
        return {
          ...prevData,
          symptoms: [...prevData.symptoms, selectedSymptom],
          symptom_ids: [...prevData.symptom_ids, selectedSymptom.id],
        };
      }
      return prevData;
    });
  };

  const handleRemoveSymptom = (id) => {
    setData((prevData) => ({
      ...prevData,
      symptoms: prevData.symptoms.filter((symptom) => symptom.id !== id),
      symptom_ids: prevData.symptom_ids.filter((symptomId) => symptomId !== id),
    }));
  };

  return (
    <div className="App">
      <main style={{ padding: isMobile ? "10px 5px" : "20px 5px" }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={6}>
            <Avatar
              alt="Image"
              src={data.imageUrl || "/placeholder.jpg"}
              style={{
                width: 100,
                height: 100,
                margin: "0 auto",
                marginBottom: 20,
              }}
            />
            <Card style={{ margin: "0 auto" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  color="#272727"
                  align="center"
                  gutterBottom
                >
                  Category Details View
                </Typography>
                <form onSubmit={handleSave}>
                  <Grid container spacing={isMobile ? 2 : 4}>
                    <Grid item xs={12}>
                      <TextField
                        label="ID"
                        name="id"
                        value={data.id || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        disabled
                      />
                      <TextField
                        label="Name"
                        name="name"
                        value={data.name || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        label="Description"
                        name="description"
                        value={data.description || ""}
                        onChange={handleInputChange}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={isMobile ? 2 : 4}
                        style={{ marginTop: "16px" }}
                      />
                      <TextField
                        fullWidth
                        label="Image URL"
                        name="imageUrl"
                        value={uploadedImageUrl}
                        onChange={handleInputChange}
                        style={{ marginTop: "16px" }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => setOpenModal(true)}
                        color="primary"
                        aria-label="upload image"
                      >
                        <PhotoCamera />
                      </IconButton>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography>Symptoms</Typography>
                      <SymptomSelector onSelectSymptom={handleSelectSymptom} />
                      {data.symptoms.map((symptom) => (
                        <Chip
                          key={symptom.id}
                          label={symptom.name}
                          onDelete={() => handleRemoveSymptom(symptom.id)}
                        />
                      ))}
                    </Grid>
                    <Grid container justifyContent="center">
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          sx={{
                            borderRadius: 15,
                            boxShadow: "none",
                            width: isMobile ? "100%" : "200px",
                            marginTop: "16px",
                          }}
                        >
                          Update
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{
                            borderRadius: 15,
                            boxShadow: "none",
                            width: isMobile ? "100%" : "200px",
                            marginTop: "16px",
                            marginLeft: "16px",
                          }}
                          onClick={handleDeleteDialogOpen}
                        >
                          Delete
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
                <DynamicUploadModal
                  open={openModal}
                  onClose={handleModalClose}
                  uploadPreset="bookapp"
                  onUploadSuccess={handleUploadSuccess}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </main>

      <Snackbar
        open={showAlert}
        autoHideDuration={6000}
        onClose={handleAlertClose}
      >
        <Alert
          onClose={handleAlertClose}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please type "DELETE" to confirm the deletion of this record.
          </DialogContentText>
          <TextField
            value={deleteConfirmation}
            onChange={handleDeleteConfirmationChange}
            fullWidth
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleteConfirmation !== "DELETE"}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Fullview;
